import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, X, Check, Mail } from 'lucide-react';
import { Loan } from '../data';
interface SOSMarkModalProps {
  loan: Loan;
  alwaysIncludedRecipients: string[];
  onClose: () => void;
  onConfirm: (note: string, recipients: string[]) => void;
}
interface LoanRecipient {
  /** Role/relationship to the loan, e.g. "Loan Owner". */
  role: string;
  /** Person's name. */
  name: string;
  /** Display string used as recipient identifier. */
  label: string;
}
export function SOSMarkModal({
  loan,
  alwaysIncludedRecipients,
  onClose,
  onConfirm
}: SOSMarkModalProps) {
  const [note, setNote] = useState('');
  // Build the recipient list FROM THIS LOAN's stakeholders only.
  // De-duplicated by name (e.g. if Loan Owner === Team Lead like a working manager).
  const loanRecipients: LoanRecipient[] = useMemo(() => {
    const candidates: LoanRecipient[] = [{
      role: 'Loan Owner',
      name: loan.loanOwner,
      label: `${loan.loanOwner} (Loan Owner)`
    }, {
      role: 'Team Lead',
      name: loan.teamLead,
      label: `${loan.teamLead} (Team Lead)`
    }, {
      role: 'Loan Processor',
      name: loan.processor,
      label: `${loan.processor} (Loan Processor)`
    }, {
      role: 'Processor Assistant',
      name: loan.processorAssistant,
      label: `${loan.processorAssistant} (Processor Assistant)`
    }].filter(r => r.name);
    // De-dupe by name — keep the first matching role.
    const seen = new Set<string>();
    return candidates.filter(r => {
      if (seen.has(r.name)) return false;
      seen.add(r.name);
      return true;
    });
  }, [loan]);
  // Default to all loan stakeholders selected — typical SOS escalates to everyone on the file.
  const [recipients, setRecipients] = useState<string[]>(() => loanRecipients.map(r => r.label));
  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [onClose]);
  const toggleRecipient = (r: string) => {
    setRecipients(prev => prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r]);
  };
  const canSubmit = note.trim().length > 0;
  const totalSelected = recipients.length + alwaysIncludedRecipients.length;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm" data-id="element-386">
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-xl flex flex-col max-h-[90vh] overflow-hidden border border-sos-200 dark:border-sos-900/50 animate-in fade-in zoom-in-95 duration-200" data-id="element-387">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-800 bg-sos-50 dark:bg-sos-900/20" data-id="element-388">
          <div className="flex items-center space-x-3" data-id="element-389">
            <div className="w-8 h-8 rounded-full bg-sos-500 flex items-center justify-center" data-id="element-390">
              <AlertTriangle className="w-4 h-4 text-white" data-id="element-391" />
            </div>
            <div data-id="element-392">
              <h2 className="text-base font-semibold text-slate-900 dark:text-white" data-id="element-393">
                Flag SOS — {loan.loanName}
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400" data-id="element-394">
                Document the urgent issue and notify file stakeholders
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 rounded-full hover:bg-white/60 dark:hover:bg-slate-800 transition-colors" data-id="element-395">
            <X className="w-5 h-5" data-id="element-396" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto space-y-5" data-id="element-397">
          <div data-id="element-398">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5" data-id="element-399">
              SOS Note <span className="text-sos-500" data-id="element-400">*</span>
            </label>
            <textarea autoFocus rows={4} value={note} onChange={e => setNote(e.target.value)} placeholder="Describe the issue requiring escalation…" className="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-sos-500 focus:border-sos-500 outline-none dark:text-white resize-none" data-id="element-401" />
          </div>

          <div data-id="element-402">
            <div className="flex items-center justify-between mb-2" data-id="element-403">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center" data-id="element-404">
                <Mail className="w-3.5 h-3.5 mr-1.5 text-slate-400" data-id="element-405" />
                Notify recipients
              </label>
              <span className="text-xs text-slate-400" data-id="element-406">
                {totalSelected} selected
              </span>
            </div>

            {alwaysIncludedRecipients.length > 0 && <div className="mb-2 px-3 py-2 bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-md" data-id="element-407">
                <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 mb-1.5" data-id="element-408">
                  Always Included (Admin)
                </div>
                <div className="flex flex-wrap gap-1.5" data-id="element-409">
                  {alwaysIncludedRecipients.map(r => <span key={r} className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-cobalt-100 text-cobalt-700 dark:bg-cobalt-900/40 dark:text-cobalt-300" data-id="element-410">
                      <Check className="w-3 h-3 mr-1" data-id="element-411" />
                      {r}
                    </span>)}
                </div>
              </div>}

            <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 mb-1.5" data-id="element-412">
              File Stakeholders
            </div>
            <div className="border border-slate-200 dark:border-slate-700 rounded-md divide-y divide-slate-100 dark:divide-slate-800" data-id="element-413">
              {loanRecipients.map(r => {
              const checked = recipients.includes(r.label);
              return <button key={r.label} type="button" onClick={() => toggleRecipient(r.label)} className="w-full flex items-center gap-3 px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/60 text-left transition-colors" data-id="element-414">
                    <span className={`flex items-center justify-center w-4 h-4 rounded border flex-shrink-0 ${checked ? 'bg-cobalt-600 border-cobalt-600 text-white' : 'border-slate-300 dark:border-slate-600 text-transparent'}`} data-id="element-415">
                      <Check className="w-3 h-3" strokeWidth={3} data-id="element-416" />
                    </span>
                    <span className="flex-1 min-w-0 flex items-center justify-between gap-3" data-id="element-417">
                      <span className="truncate" data-id="element-418">{r.name}</span>
                      <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 dark:text-slate-500 flex-shrink-0" data-id="element-419">
                        {r.role}
                      </span>
                    </span>
                  </button>;
            })}
            </div>
            <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-1.5" data-id="element-420">
              Recipients are scoped to this loan file only.
            </p>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 flex justify-end space-x-2" data-id="element-421">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-md transition-colors" data-id="element-422">
            Cancel
          </button>
          <button onClick={() => onConfirm(note.trim(), recipients)} disabled={!canSubmit} className="px-4 py-2 text-sm font-medium bg-sos-600 hover:bg-sos-500 text-white rounded-md transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center" data-id="element-423">
            <AlertTriangle className="w-4 h-4 mr-1.5" data-id="element-424" />
            Flag SOS & Send
          </button>
        </div>
      </div>
    </div>;
}